import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  Stack,
  Button,
  Typography,
  Grid,
  Backdrop,
  CircularProgress,
  Modal,
  Box,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import MUIDataTable from 'mui-datatables';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { FetchContext } from 'src/context/FetchContext';
import { AuthContext } from 'src/context/AuthContext';
import { API_ROUTES } from 'src/services/constant';

const { PRIVATE_ROUTE } = API_ROUTES;

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';

export default function Holidays() {
  const { authAxios } = useContext(FetchContext);
  const { state } = useContext(AuthContext);
  const isAdmin = state?.user?.role === 'admin';
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [holidays, setHolydays] = useState([]);
  const [rawHolydays, setRawHolydays] = useState([]);
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState({ _id: '', date: '', description: '' });
  const [editErrors, setEditErrors] = useState({});
  const [addModal, setAddModal] = useState(false);
  const [addData, setAddData] = useState({ date: '', description: '' });
  const [addErrors, setAddErrors] = useState({});

  const fetchHolydays = async () => {
    setLoading(true);
    const { data } = await authAxios.get(PRIVATE_ROUTE.GET_HOLIDAYS);
    const raw = data?.data ?? [];
    setRawHolydays(raw);
    applyYearFilter(raw, selectedYear);
    setLoading(false);
  };

  const applyYearFilter = (raw, year) => {
    const filtered = raw
      .filter((item) => item.date && parseInt(item.date.split('T')[0].split('-')[0], 10) === year)
      .map((item) => {
        const [y, m, d] = item.date.split('T')[0].split('-').map(Number);
        return { ...item, _dateObj: new Date(y, m - 1, d) };
      });

    const sorted = filtered.sort((a, b) => a._dateObj - b._dateObj);

    setHolydays(
      sorted.map((item) => ({
        _id: item._id,
        date: item._dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        description: item.description,
      }))
    );
  };

  useEffect(() => {
    fetchHolydays();
  }, []);

  useEffect(() => {
    applyYearFilter(rawHolydays, selectedYear);
  }, [selectedYear, rawHolydays]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      await authAxios.post(`${PRIVATE_ROUTE.DELETE_HOLIDAY}${id}`);
      fetchHolydays();
    }
  };

  const handleEditOpen = (id) => {
    const item = rawHolydays.find((h) => h._id === id);
    if (!item) return;
    setEditData({
      _id: item._id,
      date: item.date ? item.date.split('T')[0] : '',
      description: item.description,
    });
    setEditErrors({});
    setEditModal(true);
  };

  const handleAddSave = async () => {
    const errors = {};
    if (!addData.date) errors.date = 'Date is required';
    if (!addData.description.trim()) errors.description = 'Description is required';
    if (!errors.date) {
      const isDuplicate = rawHolydays.some(
        (h) => !h.is_deleted && h.date && h.date.split('T')[0] === addData.date
      );
      if (isDuplicate) errors.date = 'A holiday already exists for this date';
    }
    if (Object.keys(errors).length) { setAddErrors(errors); return; }

    await authAxios.post(PRIVATE_ROUTE.CREATE_HOLIDAY, {
      date: addData.date,
      description: addData.description,
    });
    setAddModal(false);
    setAddData({ date: '', description: '' });
    fetchHolydays();
  };

  const handleEditSave = async () => {
    const errors = {};
    if (!editData.date) errors.date = 'Date is required';
    if (!editData.description.trim()) errors.description = 'Description is required';
    if (!errors.date) {
      const isDuplicate = rawHolydays.some(
        (h) => !h.is_deleted && h._id !== editData._id && h.date && h.date.split('T')[0] === editData.date
      );
      if (isDuplicate) errors.date = 'A holiday already exists for this date';
    }
    if (Object.keys(errors).length) { setEditErrors(errors); return; }

    await authAxios.post(`${PRIVATE_ROUTE.EDIT_HOLIDAY}${editData._id}`, {
      date: editData.date,
      description: editData.description,
    });
    setEditModal(false);
    fetchHolydays();
  };

  const columns = [
    {
      name: '_id',
      label: '_id',
      options: { display: false, filter: false, sort: false },
    },
    {
      name: 'date',
      label: 'Date',
      options: {
        draggable: true, sort: true, filter: true, display: true,
        setCellHeaderProps: () => ({ style: { whiteSpace: 'nowrap' } }),
        setCellProps: () => ({ style: { whiteSpace: 'nowrap' } }),
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        draggable: true, sort: false, filter: true, display: true,
        setCellProps: () => ({ style: { wordBreak: 'break-word', whiteSpace: 'normal' } }),
      },
    },
    ...(isAdmin ? [{
      name: '',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        display: true,
        setCellHeaderProps: () => ({ style: { whiteSpace: 'nowrap', minWidth: 90 } }),
        setCellProps: () => ({ style: { minWidth: 90, padding: '8px 16px' } }),
        customBodyRender: (_, tableMeta) => (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
            <EditIcon
              sx={{ cursor: 'pointer', color: '#000', fontSize: 20, flexShrink: 0 }}
              onClick={() => handleEditOpen(tableMeta.rowData[0])}
            />
            <DeleteIcon
              sx={{ cursor: 'pointer', color: '#000', fontSize: 20, flexShrink: 0 }}
              onClick={() => handleDelete(tableMeta.rowData[0])}
            />
          </div>
        ),
      },
    }] : []),
  ];

  const options = {
    selectToolbarPlacement: 'none',
    rowHover: false,
    jumpToPage: true,
    responsive: 'standard',
    print: true,
    filter: false,
    fixedHeader: true,
    searchOpen: true,
    selectableRows: false,
    setRowProps: (_, _dataIndex, rowIndex) => ({
      style: { backgroundColor: rowIndex % 2 === 0 ? 'rgba(101, 200, 208, 0.15)' : 'white' },
    }),
    viewColumns: false,
    downloadOptions: { filename: `holy_days_${selectedYear}.csv` },
    setTableProps: () => ({
      padding: 'default',
      border: '1px solid #65C8D0',
      size: 'small',
    }),
  };

  return (
    <>
      <Grid sx={{ p: { xs: 1.5, sm: 4 }, pb: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
          <Typography variant="h4">Holidays</Typography>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography sx={{ fontWeight: 600, color: '#555', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Filter by Year
            </Typography>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{ fontSize: '0.85rem' }}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isAdmin && (
              <Button
                variant="contained"
                onClick={() => { setAddData({ date: '', description: '' }); setAddErrors({}); setAddModal(true); }}
                sx={{ backgroundColor: TEAL_DARK, fontWeight: 700, '&:hover': { backgroundColor: TEAL }, boxShadow: 'none' }}
              >
                New Holiday
              </Button>
            )}
          </Stack>
        </Stack>
        <Table>
          <MUIDataTable options={options} columns={columns} data={holidays} />
          {loading && (
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
        </Table>
      </Grid>

      {/* Add Modal */}
      <Modal open={addModal} onClose={() => setAddModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 420 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              px: 3, py: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>New Holiday</Typography>
            <IconButton size="small" onClick={() => setAddModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              size="small"
              value={addData.date}
              onChange={(e) => setAddData({ ...addData, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              error={!!addErrors.date}
              helperText={addErrors.date}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={addData.description}
              onChange={(e) => setAddData({ ...addData, description: e.target.value })}
              error={!!addErrors.description}
              helperText={addErrors.description}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
            />
          </Box>
          <Box
            sx={{
              px: 3, py: 2,
              borderTop: `1px solid ${TEAL_LIGHT}`,
              backgroundColor: '#fafafa',
              display: 'flex', justifyContent: 'flex-end', gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setAddModal(false)}
              sx={{ borderColor: TEAL_DARK, color: TEAL_DARK, '&:hover': { borderColor: TEAL, backgroundColor: TEAL_LIGHT } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddSave}
              sx={{ backgroundColor: TEAL_DARK, fontWeight: 700, '&:hover': { backgroundColor: TEAL }, boxShadow: 'none' }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal open={editModal} onClose={() => setEditModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 420 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
              Edit Holiday
            </Typography>
            <IconButton size="small" onClick={() => setEditModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              size="small"
              value={editData.date}
              onChange={(e) => setEditData({ ...editData, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              error={!!editErrors.date}
              helperText={editErrors.date}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={editData.description}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              error={!!editErrors.description}
              helperText={editErrors.description}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
            />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${TEAL_LIGHT}`,
              backgroundColor: '#fafafa',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setEditModal(false)}
              sx={{ borderColor: TEAL_DARK, color: TEAL_DARK, '&:hover': { borderColor: TEAL, backgroundColor: TEAL_LIGHT } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSave}
              sx={{ backgroundColor: TEAL_DARK, fontWeight: 700, '&:hover': { backgroundColor: TEAL }, boxShadow: 'none' }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
